import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight, Check, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { outlineOnDarkClass } from '@/lib/ctaStyles';
import { inputClass, labelClass } from '@/components/forms/leadFormStyles';
import { useLeadFormSubmit } from '@/hooks/useLeadFormSubmit';
import { t } from '@/i18n';
import {
  NAIC_CHECKLIST_CANONICAL,
  NAIC_CHECKLIST_PDF_PATH,
  NAIC_CHECKLIST_ROLES,
  NAIC_CHECKLIST_STATS,
  NAIC_CHECKLIST_WHAT_YOU_GET,
  naicChecklistLinks as links,
} from './naicChecklistLinks';

const EMPTY_FORM = {
  firstName: '',
  email: '',
  company: '',
  role: '',
  website: '', // honeypot
};

/**
 * /lighthouse/naic-checklist — single-screen lead magnet for the NAIC AI
 * Readiness Checklist PDF. Rendered without the site chrome (Layout swaps in
 * a logo-only header and no footer for this route) so the only exits are the
 * form and the logo.
 */
export function NaicChecklistPage() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const { isSubmitting, status, errorMessage, submit, reset } = useLeadFormSubmit();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submit({
      formType: 'checklist',
      subject: 'naic_checklist',
      firstName: formData.firstName,
      email: formData.email,
      company: formData.company,
      role: formData.role,
      website: formData.website,
    });
    if (success) {
      setSubmittedEmail(formData.email);
      setFormData(EMPTY_FORM);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('lighthouse.checklist.meta.title')}</title>
        <meta name="description" content={t('lighthouse.checklist.meta.description')} />
        <meta property="og:title" content={t('lighthouse.checklist.meta.title')} />
        <meta property="og:description" content={t('lighthouse.checklist.meta.description')} />
        <link rel="canonical" href={NAIC_CHECKLIST_CANONICAL} />
      </Helmet>

      <div className="flex min-h-[calc(100vh-4rem)] flex-col bg-secondary text-secondary-foreground">
        {status === 'success' ? (
          <Confirmation email={submittedEmail} />
        ) : (
          <>
            {/* Hero + form */}
            <section className="flex-1">
              <div className="container mx-auto grid gap-12 px-4 py-14 lg:grid-cols-2 lg:gap-14 lg:py-16">
                <div>
                  <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
                    {t('lighthouse.checklist.hero.eyebrow')}
                  </span>
                  <h1 className="mt-4 max-w-[18ch] font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
                    {t('lighthouse.checklist.hero.headline')}
                  </h1>
                  <p className="mt-5 max-w-[30em] text-lg leading-relaxed text-secondary-foreground/80 sm:text-xl">
                    {t('lighthouse.checklist.hero.subhead')}
                  </p>
                  <ul className="mt-8 grid gap-3.5 text-base text-secondary-foreground/90 sm:text-lg">
                    {NAIC_CHECKLIST_WHAT_YOU_GET.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check className="mt-1 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                        <span>{t(`lighthouse.checklist.hero.whatYouGet.${item}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="w-full max-w-[480px] rounded-xl bg-card p-6 text-card-foreground shadow-2xl shadow-black/30 sm:p-8 lg:justify-self-end">
                  {status === 'error' ? (
                    <div className="py-6 text-center">
                      <AlertCircle className="mx-auto mb-4 h-14 w-14 text-destructive" />
                      <h2 className="mb-2 text-xl font-bold">
                        {t('lighthouse.checklist.form.errorTitle')}
                      </h2>
                      <p className="mb-6 text-muted-foreground">
                        {errorMessage || t('lighthouse.checklist.form.errorFallback')}
                      </p>
                      <Button onClick={reset}>{t('lighthouse.checklist.form.tryAgain')}</Button>
                    </div>
                  ) : (
                    <form className="grid gap-4" onSubmit={handleSubmit}>
                      <h2 className="font-display text-xl font-bold tracking-tight">
                        {t('lighthouse.checklist.form.title')}
                      </h2>

                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />

                      <label className={labelClass}>
                        {t('lighthouse.checklist.form.firstName')}
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          autoComplete="given-name"
                          placeholder={t('lighthouse.checklist.form.firstNamePlaceholder')}
                          className={inputClass}
                        />
                      </label>

                      <label className={labelClass}>
                        {t('lighthouse.checklist.form.email')}
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          autoComplete="email"
                          placeholder={t('lighthouse.checklist.form.emailPlaceholder')}
                          className={inputClass}
                        />
                      </label>

                      <label className={labelClass}>
                        {t('lighthouse.checklist.form.company')}
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          autoComplete="organization"
                          placeholder={t('lighthouse.checklist.form.companyPlaceholder')}
                          className={inputClass}
                        />
                      </label>

                      <label className={labelClass}>
                        {t('lighthouse.checklist.form.role')}
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          required
                          className={inputClass}
                        >
                          <option value="" disabled>
                            {t('lighthouse.checklist.form.rolePlaceholder')}
                          </option>
                          {NAIC_CHECKLIST_ROLES.map((role) => {
                            const label = t(`lighthouse.checklist.form.roles.${role}`);
                            return (
                              <option key={role} value={label}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </label>

                      <Button type="submit" size="lg" className="mt-1" disabled={isSubmitting}>
                        {isSubmitting
                          ? t('lighthouse.checklist.form.submitting')
                          : t('lighthouse.checklist.form.submit')}
                      </Button>
                      <p className="text-center text-xs leading-relaxed text-muted-foreground">
                        {t('lighthouse.checklist.form.reassurance')}
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </section>

            {/* Stat strip */}
            <section className="border-t border-secondary-foreground/10 bg-[hsl(var(--secondary)/0.6)]">
              <div className="container mx-auto grid gap-8 px-4 py-9 md:grid-cols-3">
                {NAIC_CHECKLIST_STATS.map((stat) => (
                  <div key={stat} className="flex items-start gap-4">
                    <div className="min-w-[4rem] shrink-0 font-display text-4xl font-extrabold leading-none text-primary">
                      {t(`lighthouse.checklist.stats.${stat}.value`)}
                    </div>
                    <div className="text-secondary-foreground/80">
                      {t(`lighthouse.checklist.stats.${stat}.label`)}
                      {stat !== 'scope' && (
                        <span className="mt-1 block font-mono text-xs text-secondary-foreground/55">
                          {t(`lighthouse.checklist.stats.${stat}.note`)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <ChecklistFooter />
      </div>
    </>
  );
}

type ConfirmationKey =
  | 'headlinePrefix'
  | 'openPdf'
  | 'stuckBefore'
  | 'stuckItem10'
  | 'stuckBetween'
  | 'stuckItem15'
  | 'stuckAfter'
  | 'bookBriefing'
  | 'seeLighthouse';

function Confirmation({ email }: { email: string }) {
  const c = (key: ConfirmationKey) => t(`lighthouse.checklist.confirmation.${key}`);
  return (
    <section className="flex flex-1 items-center">
      <div className="container mx-auto max-w-3xl px-4 py-16">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-primary/50 bg-primary/15">
          <Check className="h-7 w-7 text-primary" aria-hidden="true" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
          {c('headlinePrefix')}
          <span className="break-all text-primary">{email}</span>.
        </h1>
        <div className="mt-6">
          <Button size="lg" asChild>
            <a href={NAIC_CHECKLIST_PDF_PATH} target="_blank" rel="noopener">
              <FileText aria-hidden="true" />
              {c('openPdf')}
            </a>
          </Button>
        </div>

        <div className="mt-11 border-t border-secondary-foreground/15 pt-8">
          <p className="max-w-[34em] text-lg leading-relaxed text-secondary-foreground/85">
            {c('stuckBefore')}
            <strong className="text-secondary-foreground">{c('stuckItem10')}</strong>
            {c('stuckBetween')}
            <strong className="text-secondary-foreground">{c('stuckItem15')}</strong>
            {c('stuckAfter')}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            <Button size="lg" variant="outline" className={outlineOnDarkClass} asChild>
              <Link to={links.briefingFromLanding}>{c('bookBriefing')}</Link>
            </Button>
            <Link
              to={links.lighthouseFromLanding}
              className="inline-flex items-center gap-2 font-semibold text-primary hover:underline"
            >
              {c('seeLighthouse')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChecklistFooter() {
  return (
    <footer className="border-t border-secondary-foreground/10">
      <div className="container mx-auto px-4 py-5 text-xs leading-relaxed text-secondary-foreground/55">
        {t('lighthouse.checklist.footer.disclaimer')} {t('lighthouse.checklist.footer.copyright')}
      </div>
    </footer>
  );
}
